/**
 * Account Filters Component
 *
 * Filter toolbar for the accounts list with search, status, plan, and billing filters.
 */
import { useState, useEffect } from 'react';
import { Input, Button, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@respectly/ui';
import { Search, X } from 'lucide-react';
import { cn } from '@respectly/ui';
import type { TenantStatus, BillingStatus, TenantSource } from '@respectly/shared-types';
import { useAccountsStore } from '../../stores/useAccountsStore';

// ============================================
// Types
// ============================================

interface Plan {
  id: string;
  name: string;
}

interface AccountFiltersProps {
  plans?: Plan[];
  className?: string;
}

// ============================================
// Filter Options
// ============================================

const ALL_VALUE = '__all__';

const statusOptions: { value: TenantStatus | typeof ALL_VALUE; label: string }[] = [
  { value: ALL_VALUE, label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'trial', label: 'Trial' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'cancelled', label: 'Cancelled' },
];

const billingOptions: { value: BillingStatus | typeof ALL_VALUE; label: string }[] = [
  { value: ALL_VALUE, label: 'All Billing' },
  { value: 'current', label: 'Current' },
  { value: 'past_due', label: 'Past Due' },
  { value: 'grace_period', label: 'Grace Period' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'pending_card', label: 'Pending Card' },
];

const sourceOptions: { value: TenantSource | typeof ALL_VALUE; label: string }[] = [
  { value: ALL_VALUE, label: 'All Sources' },
  { value: 'signup', label: 'Signup' },
  { value: 'referral', label: 'Referral' },
  { value: 'sales', label: 'Sales' },
  { value: 'import', label: 'Import' },
];

const dateRangeOptions = [
  { value: ALL_VALUE, label: 'All Time' },
  { value: '7days', label: 'Last 7 Days' },
  { value: '30days', label: 'Last 30 Days' },
  { value: '90days', label: 'Last 90 Days' },
];

// ============================================
// Component
// ============================================

export function AccountFilters({ plans = [], className }: AccountFiltersProps) {
  const { filters, setFilter, clearFilters } = useAccountsStore();

  // Local search state for debouncing
  const [searchValue, setSearchValue] = useState(filters.search || '');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== filters.search) {
        setFilter('search', searchValue || undefined);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue, filters.search, setFilter]);

  // Check if any filters are active
  const hasActiveFilters =
    filters.search ||
    filters.status ||
    filters.billingStatus ||
    filters.planId ||
    filters.source ||
    (filters.dateRange && filters.dateRange !== 'all');

  // Active filter count
  const activeFilterCount = [
    filters.status,
    filters.billingStatus,
    filters.planId,
    filters.source,
    filters.dateRange && filters.dateRange !== 'all' ? filters.dateRange : null,
  ].filter(Boolean).length;

  // ============================================
  // Handlers
  // ============================================

  const handleClearFilters = () => {
    setSearchValue('');
    clearFilters();
  };

  // ============================================
  // Render
  // ============================================

  return (
    <div className={cn('space-y-4', className)}>
      {/* Search and Quick Filters Row */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by name, email, user, phone, ID, business..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="pl-10 pr-10"
          />
          {searchValue && (
            <button
              onClick={() => setSearchValue('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Filter Dropdowns */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Status Filter */}
          <Select
            value={filters.status || ALL_VALUE}
            onValueChange={(value) =>
              setFilter('status', value === ALL_VALUE ? undefined : (value as TenantStatus))
            }
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Plan Filter */}
          {plans.length > 0 && (
            <Select
              value={filters.planId || ALL_VALUE}
              onValueChange={(value) => setFilter('planId', value === ALL_VALUE ? undefined : value)}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>All Plans</SelectItem>
                {plans.map((plan) => (
                  <SelectItem key={plan.id} value={plan.id}>
                    {plan.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Billing Filter */}
          <Select
            value={filters.billingStatus || ALL_VALUE}
            onValueChange={(value) =>
              setFilter('billingStatus', value === ALL_VALUE ? undefined : (value as BillingStatus))
            }
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Billing" />
            </SelectTrigger>
            <SelectContent>
              {billingOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Source Filter */}
          <Select
            value={filters.source || ALL_VALUE}
            onValueChange={(value) =>
              setFilter('source', value === ALL_VALUE ? undefined : (value as TenantSource))
            }
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              {sourceOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Date Range Filter */}
          <Select
            value={filters.dateRange || ALL_VALUE}
            onValueChange={(value) =>
              setFilter('dateRange', value === ALL_VALUE ? undefined : (value as 'all' | '7days' | '30days' | '90days'))
            }
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Date Range" />
            </SelectTrigger>
            <SelectContent>
              {dateRangeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              className="text-slate-500 hover:text-slate-700"
            >
              <X className="mr-1 h-4 w-4" />
              Clear
              {activeFilterCount > 0 && (
                <span className="ml-1 rounded-full bg-slate-200 px-1.5 text-xs dark:bg-slate-700">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-500">Active filters:</span>

          {filters.search && (
            <FilterTag
              label={`Search: "${filters.search}"`}
              onRemove={() => {
                setSearchValue('');
                setFilter('search', undefined);
              }}
            />
          )}

          {filters.status && (
            <FilterTag
              label={`Status: ${statusOptions.find((o) => o.value === filters.status)?.label}`}
              onRemove={() => setFilter('status', undefined)}
            />
          )}

          {filters.billingStatus && (
            <FilterTag
              label={`Billing: ${billingOptions.find((o) => o.value === filters.billingStatus)?.label}`}
              onRemove={() => setFilter('billingStatus', undefined)}
            />
          )}

          {filters.planId && (
            <FilterTag
              label={`Plan: ${plans.find((p) => p.id === filters.planId)?.name || filters.planId}`}
              onRemove={() => setFilter('planId', undefined)}
            />
          )}

          {filters.source && (
            <FilterTag
              label={`Source: ${sourceOptions.find((o) => o.value === filters.source)?.label}`}
              onRemove={() => setFilter('source', undefined)}
            />
          )}

          {filters.dateRange && filters.dateRange !== 'all' && (
            <FilterTag
              label={`Date: ${dateRangeOptions.find((o) => o.value === filters.dateRange)?.label}`}
              onRemove={() => setFilter('dateRange', undefined)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// Filter Tag Subcomponent
// ============================================

interface FilterTagProps {
  label: string;
  onRemove: () => void;
}

function FilterTag({ label, onRemove }: FilterTagProps) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
      {label}
      <button
        onClick={onRemove}
        className="ml-0.5 rounded-full p-0.5 hover:bg-slate-200 dark:hover:bg-slate-700"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}
